import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import MultiTable from "../../../Common/MUITable";
import { getAllComplaintCircles } from "../../../Apis/complaintApi";
import { getIecActivityReport } from "../../../Apis/iecActivityApi";
import { DEFAULT_IEC_ACTIVITY_FILTERS } from "./staticReportData";
import IecActivityFilter from "./IecActivityFilter";
import DelhiReportExport from "./DelhiReportExport";

const mapCircleToWardOption = (circle) => ({
  wardId: String(circle.circleId ?? ""),
  wardName: circle.circleName ?? "",
  circleId: String(circle.circleId ?? ""),
  zoneId: String(circle.zoneId ?? ""),
});

const thumbnailImageStyle = {
  width: 28,
  height: 28,
  objectFit: "cover",
  borderRadius: 4,
  display: "block",
  margin: "0 auto",
  cursor: "pointer",
};

const withNAFallback = (field) => (row) => row[field] || "N/A";

const exportColumns = [
  { label: "Zone", field: "zoneName", render: withNAFallback("zoneName") },
  { label: "Ward", field: "wardName", render: withNAFallback("wardName") },
  {
    label: "Date Of Creation",
    field: "dateOfCreation",
    render: withNAFallback("dateOfCreation"),
  },
  {
    label: "Activity Type",
    field: "activityType",
    render: withNAFallback("activityType"),
  },
  {
    label: "Conducted Area",
    field: "conductedArea",
    render: withNAFallback("conductedArea"),
  },
  { label: "Location", field: "location", render: withNAFallback("location") },
  { label: "Remarks", field: "remarks", render: withNAFallback("remarks") },
];

const DEFAULT_PAGE_SIZE = 10;

const IecActivityReport = () => {
  const [filters, setFilters] = useState(DEFAULT_IEC_ACTIVITY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(
    DEFAULT_IEC_ACTIVITY_FILTERS
  );
  const [wardOptions, setWardOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [previewImage, setPreviewImage] = useState("");

  const tableColumns = [
    {
      label: "Zone",
      field: "zoneName",
      width: "140px",
    },
    {
      label: "Ward",
      field: "wardName",
      width: "110px",
    },
    {
      label: "Date Of Creation",
      field: "dateOfCreation",
      width: "180px",
    },
    {
      label: "Activity Type",
      field: "activityType",
      width: "400px",
    },
    {
      label: "Conducted Area",
      field: "conductedArea",
      width: "150px",
    },
    {
      label: "Location",
      field: "location",
      width: "300px",
    },
    {
      label: "Remarks",
      field: "remarks",
      width: "180px",
      render: (row) => {
        const remarks = row.remarks || "N/A";
        return (
          <span
            title={remarks}
            style={{
              display: "block",
              maxWidth: "180px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: remarks !== "N/A" ? "pointer" : "default",
            }}
          >
            {remarks}
          </span>
        );
      },
    },
    {
      label: "Image",
      field: "image",
      width: "100px",
      render: (row) =>
        row.image || row.image1 ? (
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {row.image ? (
              <img
                src={row.image}
                alt="IEC Activity"
                onClick={() => setPreviewImage(row.image)}
                style={thumbnailImageStyle}
              />
           ) :(
                <span>N/A</span>
              )}
            {row.image1 ? ( 
              <img
                src={row.image1}
                alt="IEC Activity"
                onClick={() => setPreviewImage(row.image1)}
                style={thumbnailImageStyle}
              />
            ) : (
                <span>N/A</span>
              )}
          </div>
        ) : (
          "N/A"
        ),
    },
  ];

  const loadReport = async ({
    nextFilters = appliedFilters,
    nextPage = page,
    nextPageSize = pageSize,
  } = {}) => {
    setIsLoading(true);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(nextPage);
    setPageSize(nextPageSize);

    try {
      const response = await getIecActivityReport({
        ...nextFilters,
        pageNumber: nextPage + 1,
        pageSize: nextPageSize,
      });
      setRows(response.data);
      setTotalRecords(response.totalRecords);
    } catch (error) {
      console.error("Failed to load IEC Activity Report:", error);
      setRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadWards = async () => {
      try {
        const circles = await getAllComplaintCircles();
        setWardOptions(
          circles
            .filter((circle) => circle.isActive !== false)
            .map(mapCircleToWardOption)
        );
      } catch (error) {
        console.error("Error fetching wards:", error);
        setWardOptions([]);
      }
    };

    loadWards();
    loadReport({
      nextFilters: DEFAULT_IEC_ACTIVITY_FILTERS,
      nextPage: 0,
      nextPageSize: DEFAULT_PAGE_SIZE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (values) => {
    loadReport({
      nextFilters: values,
      nextPage: 0,
      nextPageSize: pageSize,
    });
  };

  const handleReset = () => {
    loadReport({
      nextFilters: DEFAULT_IEC_ACTIVITY_FILTERS,
      nextPage: 0,
      nextPageSize: pageSize,
    });
  };

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">IEC Activity Report</h5>
            <p className="text-muted mb-0">View IEC Activity Report.</p>
          </div>
          <DelhiReportExport
            reportData={rows}
            reportTitle="IEC Activity"
            columns={exportColumns}
            fileName="IecActivityReport"
          />
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
            <IecActivityFilter
              initialValues={filters}
              wardOptions={wardOptions}
              onSearch={handleSearch}
              onReset={handleReset}
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="top-header text-center">
              <span>IEC Activity Report</span>
            </div>
            <div className="table-responsive">
              <MultiTable
                loading={isLoading}
                needSRNumber
                columns={tableColumns}
                rows={rows}
                tableHeight={500}
                serverPagination
                totalCount={totalRecords}
                page={page}
                rowsPerPage={pageSize}
                onPageChange={(_event, newPage) =>
                  loadReport({
                    nextFilters: appliedFilters,
                    nextPage: newPage,
                    nextPageSize: pageSize,
                  })
                }
                onRowsPerPageChange={(event) =>
                  loadReport({
                    nextFilters: appliedFilters,
                    nextPage: 0,
                    nextPageSize: parseInt(event.target.value, 10),
                  })
                }
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={!!previewImage} onHide={() => setPreviewImage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={previewImage}
            alt="Preview"
            style={{
              width: "100%",
              maxHeight: 400,
              objectFit: "contain",
              borderRadius: 5,
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default IecActivityReport;
